import type { Severity } from "@common/client"
import { Button } from "@common/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@common/components/ui/dropdown-menu"
import { cn } from "@common/lib/utils"
import { PANE_CHIP } from "@/components/page/pane"
import {
  Ring,
  StatusSvg,
} from "@/routes/_layout/quotes/-components/status-glyphs"
import Check from "~icons/material-symbols/check"
import ExpandMore from "~icons/material-symbols/expand-more"
import { SEVERITIES, severityChoice, severityLabel } from "../-lib/status"

const TONE: Record<string, string> = {
  airworthiness: "text-semantic-error",
  informational: "text-semantic-info",
  none: "text-muted-foreground",
}

export function SeverityIcon({
  severity,
}: {
  severity: Severity | null
}): React.ReactElement {
  return (
    <StatusSvg
      label={`Severity · ${severityLabel(severity)}`}
      tone={TONE[severityChoice(severity)]}
    >
      <Glyph severity={severity} />
    </StatusSvg>
  )
}

function Glyph({
  severity,
}: {
  severity: Severity | null
}): React.ReactElement {
  switch (severity) {
    case "airworthiness":
      return (
        <>
          <path
            d="M7 1.6 13 12.2H1Z"
            fill="currentColor"
            strokeLinejoin="round"
          />
          <path
            d="M7 5.4v3.2"
            className="stroke-background"
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <circle cx={7} cy={10.4} r={0.85} className="fill-background" />
        </>
      )
    case "informational":
      return (
        <>
          <Ring />
          <path
            d="M7 6.2v3.4"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <circle cx={7} cy={4.3} r={0.85} fill="currentColor" />
        </>
      )
    case null:
      return <Ring dashed />
  }
}

export function SeverityPicker({
  severity,
  disabled,
  chip = false,
  onPick,
}: {
  severity: Severity | null
  disabled: boolean
  chip?: boolean
  onPick: (severity: Severity | null) => void
}): React.ReactElement {
  if (disabled) {
    return chip ? (
      <span className={PANE_CHIP}>
        <SeverityIcon severity={severity} />
        {severityLabel(severity)}
      </span>
    ) : (
      <span className="flex size-6 items-center justify-center">
        <SeverityIcon severity={severity} />
      </span>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={chip ? "outline" : "ghost"}
          size={chip ? "sm" : "icon-xs"}
          title={`Severity · ${severityLabel(severity)}`}
          className={cn(
            chip && "gap-1.5 px-2 text-small-medium [&_svg]:size-3.5",
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <SeverityIcon severity={severity} />
          {chip && severityLabel(severity)}
          {chip && <ExpandMore className="text-muted-foreground" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-60"
        onClick={(event) => event.stopPropagation()}
      >
        {SEVERITIES.map((option) => (
          <DropdownMenuItem
            key={severityChoice(option)}
            className="gap-2.5"
            onSelect={() => option !== severity && onPick(option)}
          >
            <SeverityIcon severity={option} />
            <span className="min-w-0 flex-1">{severityLabel(option)}</span>
            {option === severity && <Check className="size-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
